import { inputWord } from "@workforceos/shared";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../../api/client";
import { useMe } from "../../hooks/useMe";
import { usePersona } from "../../hooks/usePersona";
import { formatTime, formatWhen, todayInWorkspace } from "../../lib/when";
import { HoursByAgent } from "../../components/HoursByAgent";
import { verdictLabel, verdictTone } from "./Workforce";
import { roleLabel } from "../../lib/roleLabel";
import {
  useWorkspaceEvents,
  type WorkspaceEvent,
} from "../../hooks/useWorkspaceEvents";

/**
 * Home.
 *
 * It used to be PlayStudio's dashboard: running plays, a play decision queue,
 * and two tiles whose sparklines were literal arrays in the source with "vs
 * target" underneath and no target anywhere in the product. On a workforce
 * account nothing ever put a play there, so the first screen of the product
 * was three empty states and two invented lines.
 *
 * What it says now is what the run records say: who is waiting on a person,
 * what the agents did today, and nothing else. Every number on this page is
 * counted from rows.
 */

const MAX_ACTIVITY = 50;

interface Fork {
  run_id: string;
  hand_name: string;
  created_at: string;
  blocking_rules: { rule_id: string; summary: string; origin: string }[];
  missing_rules?: { rule_id: string; summary: string | null; origin: string | null }[];
  missing: string[];
  subject: { label: string; value: string } | null;
  decision?: { key: string; label: string; stage: string; approver_role: string } | null;
  resolution: unknown | null;
}

interface Run {
  run_id: string;
  hand_key: string;
  hand_name: string;
  verdict: string;
  created_at: string;
  llm_model: string | null;
}

interface Health {
  failures: { run_id: string; hand_name: string; created_at: string }[];
  schedules: { hand_key: string; hand_name: string; last_error: string | null }[];
}

interface RosterHand {
  key: string;
  name: string;
  last_run_at: string | null;
}

const OBLIGATION = new Set(["statutory", "regulator_rule", "sharia_ruling"]);

function needsReviewer(f: Fork): boolean {
  return (
    f.blocking_rules.some((r) => OBLIGATION.has(r.origin)) ||
    (f.missing_rules ?? []).some((r) => r.origin !== null && OBLIGATION.has(r.origin))
  );
}

/** Whose signature releases this — an obligation outranks the decision's own. */
function approverRole(f: Fork): string {
  return needsReviewer(f) ? "reviewer" : (f.decision?.approver_role ?? "reviewer");
}

/** Why this fork is open, in the words the queue uses. */
function forkReason(f: Fork): string {
  if (f.blocking_rules.length > 0) return f.blocking_rules[0]!.summary;
  const named = (f.missing_rules ?? []).find((m) => m.summary);
  if (named) return named.summary!;
  if (f.missing.length > 0) return `Waiting on: ${[...new Set(f.missing.map((m) => inputWord(m, "en")))].join("; ")}`;
  return "Needs a decision";
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function firstName(displayName: string | undefined, email: string): string {
  // Skip the displayName fallback if it's empty OR if it just echoes the
  // email (some OIDC providers return name=email when no name claim is set).
  const trimmed = displayName?.trim() ?? "";
  if (trimmed.length > 0 && trimmed.toLowerCase() !== email.toLowerCase()) {
    return trimmed.split(/\s+/)[0]!;
  }
  const local = (email.split("@")[0] ?? "there").replace(/[._-]+/g, " ");
  return local.charAt(0).toUpperCase() + local.slice(1);
}

interface ActivityItem {
  id: string;
  time: string;
  who: string;
  action: string;
  subject: string;
  agent?: string;
  /** The workforce agent behind the line; its name resolves from the roster. */
  handKey?: string;
  entering?: boolean;
}

/** The workforce topics (CH-WS-1), said the way a person would say them. */
function workforceLine(
  type: string,
  p: Record<string, unknown>,
): { who: string; action: string; subject: string; handKey?: string | undefined } | null {
  const handKey = typeof p["handKey"] === "string" ? p["handKey"] : undefined;
  const agent = handKey ?? "An agent";
  const verdictWords = (v: unknown) =>
    v === "auto_completed"
      ? "completed unattended"
      : v === "escalated"
        ? "sent to a person"
        : v === "blocked_pre"
          ? "stopped before drafting"
          : v === "failed"
            ? "failed"
            : String(v ?? "");
  switch (type) {
    case "run.started":
      return { who: agent, action: "started", subject: "", handKey };
    case "run.completed":
      return { who: agent, action: "finished —", subject: verdictWords(p["verdict"]), handKey };
    case "run.blocked":
      return {
        who: agent,
        action: "stopped at the gate",
        subject: p["reason"] === "insufficient_input" ? "missing input" : "a rule blocks it",
        handKey,
      };
    case "run.failed":
      return { who: agent, action: "could not run", subject: "", handKey };
    case "fork.opened":
      return {
        who: agent,
        action: "is waiting on",
        subject: typeof p["approverRole"] === "string" ? roleLabel(p["approverRole"]) : "a person",
        handKey,
      };
    case "fork.resolved":
      return {
        who: agent,
        action: `— ${typeof p["resolvedRole"] === "string" ? roleLabel(p["resolvedRole"]) : "someone"} decided:`,
        subject: String(p["decision"] ?? "").replace(/_/g, " "),
        handKey,
      };
    case "schedule.failed":
      return { who: agent, action: "schedule failed", subject: "", handKey };
    default:
      return null;
  }
}

function eventToActivity(e: WorkspaceEvent, idx: number): ActivityItem {
  const p = (e.payload ?? {}) as Record<string, unknown>;
  const wf = workforceLine(e.type, p);
  if (wf) {
    return {
      id: `evt-${Date.now()}-${idx}`,
      time: formatTime(new Date().toISOString()),
      who: wf.who,
      action: wf.action,
      subject: wf.subject,
      entering: true,
      ...(wf.handKey ? { handKey: wf.handKey } : {}),
    };
  }
  const who =
    (typeof p["actorName"] === "string" && p["actorName"]) ||
    (typeof p["actorKind"] === "string" && p["actorKind"]) ||
    "System";
  const subject =
    (typeof p["title"] === "string" && p["title"]) ||
    (typeof p["playId"] === "string" && `play ${p["playId"]}`) ||
    "";
  const item: ActivityItem = {
    id: `evt-${Date.now()}-${idx}`,
    time: formatTime(new Date().toISOString()),
    who: String(who),
    action: e.type,
    subject: String(subject),
    entering: true,
  };
  if (typeof p["agent"] === "string") item.agent = p["agent"];
  return item;
}

function DashboardInner() {
  const params = useParams<{ workspaceId: string }>();
  const workspaceId = params.workspaceId ?? "";
  const navigate = useNavigate();
  const me = useMe();
  const persona = usePersona(workspaceId);

  const forks = useQuery<{ forks: Fork[] }, Error>({
    queryKey: ["forks", workspaceId, false],
    enabled: !!workspaceId,
    queryFn: () => apiGet<{ forks: Fork[] }>(`/api/workspaces/${workspaceId}/forks`),
  });

  const runs = useQuery<{ runs: Run[] }, Error>({
    queryKey: ["hand-runs", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet<{ runs: Run[] }>(`/api/workspaces/${workspaceId}/hand-runs?limit=100`),
  });

  const roster = useQuery<{ hands: RosterHand[] }, Error>({
    queryKey: ["workforce", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet<{ hands: RosterHand[] }>(`/api/workspaces/${workspaceId}/workforce`),
  });

  // The agent handler holds no judgement forks — there is no business
  // decision in a malformed draft — so their home is what broke, not what is
  // waiting to be signed.
  const maintains = persona === "agent_handler";
  const health = useQuery<Health, Error>({
    queryKey: ["agent-health", workspaceId],
    enabled: !!workspaceId && maintains,
    queryFn: () => apiGet<Health>(`/api/workspaces/${workspaceId}/agent-health`),
  });

  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const onEvent = useCallback((e: WorkspaceEvent) => {
    setActivity((prev) => {
      const next = [eventToActivity(e, prev.length), ...prev];
      return next.slice(0, MAX_ACTIVITY);
    });
  }, []);
  // Names resolve at render time, so a line that arrived before the roster
  // did still reads as the agent's name once the roster is here.
  const rosterNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of roster.data?.hands ?? []) m.set(h.key, h.name);
    return m;
  }, [roster.data]);
  const { connectionStatus } = useWorkspaceEvents(workspaceId, onEvent);

  const open = useMemo(
    () => (forks.data?.forks ?? []).filter((f) => !f.resolution),
    [forks.data],
  );
  /** The ones this person can actually close — the rest are somebody's else. */
  const mine = useMemo(
    () => open.filter((f) => persona !== null && approverRole(f) === persona),
    [open, persona],
  );

  const today = todayInWorkspace();
  const todaysRuns = useMemo(
    () => (runs.data?.runs ?? []).filter((r) => r.created_at.slice(0, 10) === today),
    [runs.data, today],
  );
  const tally = useMemo(() => {
    const t = { auto: 0, person: 0, failed: 0 };
    for (const r of todaysRuns) {
      if (r.verdict === "auto_completed") t.auto += 1;
      else if (r.verdict === "failed") t.failed += 1;
      else t.person += 1;
    }
    return t;
  }, [todaysRuns]);

  const personaFirst = me.data?.user
    ? firstName(me.data.user.displayName, me.data.user.email)
    : "there";

  const brokenSchedules = (health.data?.schedules ?? []).filter((s) => s.last_error);
  const failures = health.data?.failures ?? [];

  const lede = useMemo(() => {
    if (maintains) {
      if (health.isLoading) return "Checking the agents…";
      if (failures.length === 0 && brokenSchedules.length === 0) {
        return "Nothing is broken. Every agent ran as asked.";
      }
      return `${failures.length} failed ${failures.length === 1 ? "run" : "runs"}${
        brokenSchedules.length > 0
          ? ` and ${brokenSchedules.length} ${brokenSchedules.length === 1 ? "schedule" : "schedules"} that could not run`
          : ""
      }.`;
    }
    if (forks.isLoading) return "Checking what is waiting on you…";
    if (mine.length === 1) return "1 decision needs you.";
    if (mine.length > 1) return `${mine.length} decisions need you.`;
    if (open.length > 0) {
      return open.length === 1
        ? "1 decision is open, waiting on someone else to sign."
        : `${open.length} decisions are open, waiting on someone else to sign.`;
    }
    return "All clear — nothing requires your attention.";
  }, [
    maintains,
    health.isLoading,
    failures.length,
    brokenSchedules.length,
    forks.isLoading,
    mine.length,
    open.length,
  ]);

  return (
    <div className="main-inner" data-testid="dashboard">
      <div className="greet-row">
        <div className="greet" data-testid="dashboard-greet">
          {greeting()}, {personaFirst}.
        </div>
        <div className="greet-date">{formatWhen(new Date().toISOString())}</div>
      </div>

      <div className="subgreet">
        <div className="section-eyebrow">The one thing</div>
        <div className="lede" data-testid="dashboard-lede">
          {lede}
        </div>
      </div>

      {maintains ? (
        <>
          <div className="section-h">
            <span className="ttl">What needs maintaining</span>
            <span className="meta">{failures.length} failed · {brokenSchedules.length} schedules stuck</span>
            <span
              className="right"
              role="link"
              onClick={() => navigate(`/workspaces/${workspaceId}/agent-health`)}
              style={{ cursor: "pointer" }}
            >
              Runs and schedules →
            </span>
          </div>
          <div className="decisions" data-testid="maintenance-list">
            {failures.length === 0 && brokenSchedules.length === 0 ? (
              <div className="decision-row" data-testid="maintenance-empty">
                <div className="deco-body">
                  <div className="ttl">Nothing needs you.</div>
                  <div className="meta">
                    A run stopped by a rule is not a fault — it is in someone's
                    queue with its citation.
                  </div>
                </div>
              </div>
            ) : (
              <>
                {failures.slice(0, 5).map((f) => (
                  <div className="decision-row" key={f.run_id} data-testid={`failure-${f.run_id}`}>
                    <div className="deco-icon">!</div>
                    <div className="deco-body">
                      <div className="ttl">{f.hand_name} failed</div>
                      <div className="meta">
                        <span>{formatWhen(f.created_at)}</span>
                      </div>
                    </div>
                    <div className="deco-actions">
                      <button
                        type="button"
                        className="btn primary"
                        onClick={() => navigate(`/workspaces/${workspaceId}/workforce/runs/${f.run_id}`)}
                      >
                        Open the run
                      </button>
                    </div>
                  </div>
                ))}
                {brokenSchedules.map((sch) => (
                  <div className="decision-row" key={sch.hand_key} data-testid={`stuck-${sch.hand_key}`}>
                    <div className="deco-icon">!</div>
                    <div className="deco-body">
                      <div className="ttl">{sch.hand_name} could not run</div>
                      <div className="meta">
                        <span>{sch.last_error}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      ) : (
        <>
      {/* Decisions waiting on a person */}
      <div className="section-h">
        <span className="ttl">Waiting on a person</span>
        <span className="meta" data-testid="decisions-meta">
          {open.length} open{mine.length !== open.length ? ` · ${mine.length} you can close` : ""}
        </span>
        <span
          className="right"
          role="link"
          onClick={() => navigate(`/workspaces/${workspaceId}/operator`)}
          style={{ cursor: "pointer" }}
        >
          My queue →
        </span>
      </div>
      <div className="decisions" data-testid="decisions-list">
        {forks.isLoading ? (
          <div className="decision-row" data-testid="decisions-skeleton">
            <div className="deco-body">
              <div
                className="animate-pulse"
                style={{ background: "var(--surface-2)", height: 14, width: 240, borderRadius: 4 }}
              />
            </div>
          </div>
        ) : forks.isError ? (
          <div className="decision-row" data-testid="decisions-error">
            <div className="deco-body">
              <div className="ttl">Couldn't load the queue.</div>
              <div className="meta">{forks.error?.message ?? "—"}</div>
            </div>
          </div>
        ) : open.length === 0 ? (
          <div className="decision-row" data-testid="decisions-empty">
            <div className="deco-body">
              <div className="ttl">No decisions waiting on you.</div>
              <div className="meta">
                Agents interrupt a person only at a judgement fork.
              </div>
            </div>
          </div>
        ) : (
          open.slice(0, 5).map((f) => (
            <div className="decision-row" key={f.run_id} data-testid={`decision-${f.run_id}`}>
              <div className="deco-icon">●</div>
              <div className="deco-body">
                <div className="ttl">{forkReason(f)}</div>
                <div className="meta">
                  <span>{f.hand_name}</span>
                  {f.subject ? (
                    <>
                      <span className="sep">·</span>
                      <span>
                        {f.subject.label} {f.subject.value}
                      </span>
                    </>
                  ) : null}
                  <span className="sep">·</span>
                  <span>{formatWhen(f.created_at)}</span>
                </div>
              </div>
              <div className="deco-actions">
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => navigate(`/workspaces/${workspaceId}/workforce/runs/${f.run_id}`)}
                >
                  Open the run
                </button>
              </div>
            </div>
          ))
        )}
      </div>

        </>
      )}

      {persona === "cxo" ? (
        <>
          <div className="section-h">
            <span className="ttl">Hours returned</span>
            <span className="meta">counted from the ledger · no projection</span>
          </div>
          <HoursByAgent workspaceId={workspaceId} compact />
        </>
      ) : null}

      {/* What the workforce did today */}
      <div className="section-h">
        <span className="ttl">Today</span>
        <span className="meta" data-testid="runs-meta">
          {todaysRuns.length} {todaysRuns.length === 1 ? "run" : "runs"} · counted from
          the run records
        </span>
        <span
          className="right"
          role="link"
          onClick={() => navigate(`/workspaces/${workspaceId}/workforce`)}
          style={{ cursor: "pointer" }}
        >
          Workforce →
        </span>
      </div>
      <div className="kpis" data-testid="kpis-list">
        <div className="kpi">
          <div className="lbl">Completed unattended</div>
          <div className="val" data-testid="kpi-auto">{tally.auto}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Went to a person</div>
          <div className="val" data-testid="kpi-person">{tally.person}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Failed</div>
          <div className="val" data-testid="kpi-failed">{tally.failed}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Agents on the roster</div>
          <div className="val" data-testid="kpi-agents">{roster.data?.hands.length ?? 0}</div>
        </div>
      </div>

      {todaysRuns.length > 0 ? (
        <div className="decisions" data-testid="todays-runs">
          {todaysRuns.slice(0, 6).map((r) => (
            <div
              className="decision-row"
              key={r.run_id}
              data-testid={`run-${r.run_id}`}
              onClick={() => navigate(`/workspaces/${workspaceId}/workforce/runs/${r.run_id}`)}
              style={{ cursor: "pointer" }}
            >
              <div className="deco-body">
                <div className="ttl">{r.hand_name}</div>
                <div className="meta">
                  <span>{formatWhen(r.created_at)}</span>
                  <span className="sep">·</span>
                  <span>{r.llm_model ? `drafted by ${r.llm_model}` : "no model called"}</span>
                </div>
              </div>
              <div className="deco-actions">
                <span className={`pill ${verdictTone(r.verdict)}`}>{verdictLabel(r.verdict)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="decisions">
          <div className="decision-row" data-testid="todays-runs-empty">
            <div className="deco-body">
              <div className="ttl">No runs yet today.</div>
              <div className="meta">
                Agents run on their own schedules; you can also start one from the
                roster.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team activity (live) */}
      <div className="section-h">
        <span className="ttl">Team activity</span>
        <span className="meta" data-testid="activity-meta">
          <span
            style={{
              display: "inline-block",
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background:
                connectionStatus === "open" ? "var(--accent-2)" : "var(--text-mute)",
              marginRight: "5px",
              verticalAlign: "middle",
            }}
          />
          {connectionStatus === "open" ? "live" : connectionStatus}
        </span>
      </div>
      <div className="activity" data-testid="activity-list">
        {activity.length === 0 ? (
          <div className="activity-row" data-testid="activity-empty">
            <span className="time">—</span>
            <span>
              <span className="who">No activity yet.</span> Events will appear here as
              work happens.
            </span>
          </div>
        ) : (
          activity.map((a) => (
            <div
              key={a.id}
              className={`activity-row ${a.entering ? "entering" : ""}`}
              data-testid={`activity-${a.id}`}
            >
              <span className="time">{a.time}</span>
              <span>
                <span className="who">
                  {a.handKey ? (rosterNames.get(a.handKey) ?? a.who) : a.who}
                </span>{" "}
                {a.action}{" "}
                <span style={{ color: "var(--text-3)" }}>{a.subject}</span>
                {a.agent ? <span className="ag-badge">via {a.agent}</span> : null}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function DashboardErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="dashboard-error">
      <div className="greet-row">
        <span className="greet">Home unavailable</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">ERROR</div>
        <div className="lede">{error.message}</div>
      </div>
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn primary" onClick={resetErrorBoundary}>
          Reload surface
        </button>
      </div>
    </div>
  );
}

export function Dashboard() {
  // Key on workspaceId so DashboardInner remounts cleanly when the user
  // switches workspaces — the activity list and the SSE subscription are
  // component-local and would otherwise carry over.
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return (
    <ErrorBoundary FallbackComponent={DashboardErrorFallback}>
      <DashboardInner key={workspaceId ?? "__no_ws__"} />
    </ErrorBoundary>
  );
}
